import { fetchGymData } from "../../../../data/APIs";
import { selectGymData } from "../../../../redux/reducers/gymDataSlice";
import {
  fetchGymCitiesData,
  fetchInHouseUsersData,
  fetchLeadSourceData,
  fetchUTMParamsData,
  fetchOperatorValuesData,
  fetchWorkoutGoalsData,
  fetchLeadFromSourceData,
  fetchLeadCaptureKeywordData,
  fetchSourceNameData,
  fetchAgentsData,
  fetchAllMembersData,
  fetchClusterManagersData,
  fetchExpenseProcessManagersData,
  fetchExpenseAccountantsData,
  fetchGymCategoriesData,
} from "../../../../redux/reducers/dropdownApiSlice";

/**
 * Registry mapping dataSource keys to their Redux fetch actions and selectors.
 *
 * Three patterns are supported:
 *  1. isLegacyThunk  — dispatch(fn(token, ...args)) pattern from APIs.js
 *  2. isQPSStyle     — payload is { token, qps, pageNo, limit }
 *  3. default        — payload is { token, filters: { ... } }
 */
const DATA_SOURCE_REGISTRY = {
  /* ---- Gym ---- */
  gym: {
    fetchAction: (token) => fetchGymData(token, 1, 100, "", "all", ""),
    selector: selectGymData,
    isLegacyThunk: true,
  },

  /* ---- City ---- */
  city: {
    fetchAction: fetchGymCitiesData,
    selector: (state) => state.dropdown?.gymCities,
    defaultPayload: { filters: { status: "active" } },
  },

  /* ---- Cluster Manager ---- */
  clusterManager: {
    fetchAction: fetchInHouseUsersData,
    selector: (state) => state.dropdown?.inhouseUsers,
    isQPSStyle: true,
    defaultParams: { user_types: "cluster_manager" },
    defaultPageNo: 1,
    defaultLimit: 100,
  },

  /* ---- In-House Users (generic) ---- */
  inHouseUsers: {
    fetchAction: fetchInHouseUsersData,
    selector: (state) => state.dropdown?.inhouseUsers,
    isQPSStyle: true,
    defaultPageNo: 1,
    defaultLimit: 100,
  },

  /* ---- Lead Source ---- */
  leadSource: {
    fetchAction: fetchLeadSourceData,
    selector: (state) => state.dropdown?.leadSources,
  },

  /* ---- Lead From Source ---- */
  leadFromSource: {
    fetchAction: fetchLeadFromSourceData,
    selector: (state) => state.dropdown?.leadFromSource,
  },

  /* ---- UTM Params ---- */
  utmParams: {
    fetchAction: fetchUTMParamsData,
    selector: (state) => state.dropdown?.utmParams,
  },

  /* ---- Operator Values ---- */
  operatorValue: {
    fetchAction: fetchOperatorValuesData,
    selector: (state) => state.dropdown?.operatorValue,
  },

  /* ---- Workout Goals ---- */
  workoutGoals: {
    fetchAction: fetchWorkoutGoalsData,
    selector: (state) => state.dropdown?.workoutGoals,
    defaultPayload: { filters: { page: 1, limit: 2000, status: "active" } },
  },

  /* ---- Lead Capture Keyword ---- */
  leadCaptureKeyword: {
    fetchAction: fetchLeadCaptureKeywordData,
    selector: (state) => state.dropdown?.leadCaptureKeywords,
  },

  /* ---- Source Name ---- */
  sourceName: {
    fetchAction: fetchSourceNameData,
    selector: (state) => state.dropdown?.sourceNames,
  },

  /* ---- Agents ---- */
  agents: {
    fetchAction: fetchAgentsData,
    selector: (state) => state.dropdown?.agents,
  },

  /* ---- Members ---- */
  members: {
    fetchAction: fetchAllMembersData,
    selector: (state) => state.dropdown?.members,
  },

  /* ---- Cluster Managers (separate list) ---- */
  clusterManagers: {
    fetchAction: fetchClusterManagersData,
    selector: (state) => state.dropdown?.clusterManagers,
  },

  /* ---- Process Managers ---- */
  processManagers: {
    fetchAction: fetchExpenseProcessManagersData,
    selector: (state) => state.dropdown?.processManagers,
    defaultPayload: {
      filters: { page: 1, limit: 1000, status: "active", role_type: "approval_manager" },
    },
  },

  /* ---- Expense Accountants ---- */
  expenseAccountants: {
    fetchAction: fetchExpenseAccountantsData,
    selector: (state) => state.dropdown?.expenseAccountants,
    defaultPayload: { filters: { page: 1, limit: 1000, status: "active" } },
  },

  /* ---- Gym Categories ---- */
  gymCategories: {
    fetchAction: fetchGymCategoriesData,
    selector: (state) => state.dropdown?.gymCategories,
  },
};

export default DATA_SOURCE_REGISTRY;
